<script setup lang="ts">
import BlockHeaderItemContent from '@zyro-inc/site-modules/components/BlockHeaderItemContent.vue';
import { SiteBlockHeaderItem } from '@zyro-inc/site-modules/types';

interface Props {
	item: SiteBlockHeaderItem;
	areDropdownsHidden?: boolean;
}

const props = defineProps<Props>();
</script>

<template>
	<li class="block-header-item">
		<label class="block-header-item__label">
			<input
				v-if="!props.areDropdownsHidden && props.item.hasDropdown"
				type="checkbox"
				class="block-header-item__mobile-dropdown-trigger"
			>
			<BlockHeaderItemContent
				v-qa="`navigation-item-${item.name}`"
				:item="item"
				class="block-header-item__item"
				:aria-haspopup="item.hasDropdown"
			/>
			<div
				v-if="!props.areDropdownsHidden && props.item.hasDropdown"
				class="block-header-item__dropdown-area"
			>
				<ul class="block-header-item__dropdown">
					<BlockHeaderItem
						v-for="subItem in props.item.subItems"
						:key="subItem.id"
						:item="subItem"
					/>
				</ul>
			</div>
		</label>
	</li>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

$underline-space: 7px;

.block-header-item {
	$this: &;

	user-select: none;

	&__dropdown-area {
		$side-padding: 16px;

		position: absolute;
		z-index: 1;
		display: none;
		padding-right: $side-padding;
		padding-bottom: 48px;
		padding-top: 8px;
	}

	&__label {
		&:hover {
			.block-header-item__dropdown-area {
				display: block;
			}

			:deep(.item-content__icon) {
				transform: rotate(180deg);
			}
		}
	}

	&__dropdown {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		padding: 16px 24px;
		list-style: none;
		background-color: var(--background-color);
		border-radius: $border-radius-small;
		box-shadow: $box-shadow;
		max-height: 400px;

		& > #{$this}:not(:last-child) {
			margin-bottom: 8px;
		}

		:deep(.item-content) {
			margin: 0;
		}

		@media screen and (min-width: $media-mobile) {
			justify-content: start;
			overflow: auto;
		}
	}

	&__mobile-dropdown-trigger {
		position: absolute;
		width: 0;
		height: 0;
		opacity: 0;
	}
}

@include site-engine-mobile {
	.block-header-item {
		&__dropdown-area {
			position: unset;
			padding: 0;
		}

		&__label {
			&:hover {
				.block-header-item__dropdown-area {
					display: none;
				}

				:deep(.item-content__icon) {
					transform: unset;
				}
			}
		}

		&__dropdown {
			padding: 0;
			background-color: transparent;
			box-shadow: none;
			margin-right: 32px;
			margin-top: var(--m-link-spacing);
			gap: var(--m-link-spacing);
			max-height: none;

			& > .block-header-item:not(:last-child) {
				margin-bottom: 0;
			}

			& > .block-header-item:last-child {
				:deep(.item-content) {
					margin: 0 0 $underline-space;
				}
			}
		}

		&__mobile-dropdown-trigger {
			display: none;

			// An invisible checkbox controls if we should show dropdown on mobile or not
			&:checked {
				+ .block-header-item__item {
					:deep(.item-content__icon) {
						transform: rotate(180deg);
					}
				}

				~ .block-header-item__dropdown-area {
					display: block;
				}
			}
		}
	}
}

</style>
